<template>
  <div :class="variant === 'default' ? 'space-y-2' : ''">
    <label
      v-if="label && variant === 'default'"
      class="block text-xs font-medium text-slate-700 dark:text-slate-200"
    >
      {{ label }}
    </label>

  <input
    ref="fileInput"
    type="file"
    class="hidden"
    :accept="accept"
    multiple
    @change="onFileChange"
  />

  <!-- Dropzone variant (comment form) -->
  <div v-if="variant === 'dropzone'" class="space-y-2">
    <!-- Selected files as chips -->
    <div
      v-if="files.length"
      class="flex flex-wrap gap-2"
    >
      <div
        v-for="(file, idx) in files"
        :key="`${file.name}-${idx}`"
        class="inline-flex items-center gap-2 pl-2 pr-1 py-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm max-w-full"
      >
        <span
          class="flex items-center justify-center w-7 h-7 shrink-0 rounded-lg"
          :class="fileIconClass(file)"
        >
          <i :class="fileIcon(file)" class="text-[14px]"></i>
        </span>
        <div class="min-w-0 flex-1">
          <p class="text-[10px] font-medium text-slate-800 dark:text-slate-100 truncate max-w-[140px]">
            {{ file.name }}
          </p>
          <p class="text-[9px] text-slate-400 tabular-nums">
            {{ formatSize(file.size) }}
          </p>
        </div>
        <button
          type="button"
          class="flex items-center justify-center w-6 h-6 shrink-0 rounded-lg text-slate-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors"
          :disabled="disabled"
          @click="removeFile(idx)"
        >
          <i class="mdi mdi-close text-[14px]"></i>
        </button>
      </div>
    </div>

    <!-- Drop zone -->
    <button
      type="button"
      class="w-full flex items-center gap-3 px-3 py-3 rounded-xl border-2 border-dashed transition-all text-start"
      :class="[
        isDragging
          ? 'border-primary bg-primary/5 scale-[1.01]'
          : 'border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/60 hover:border-primary/50 hover:bg-primary/5',
        disabled || files.length >= maxFiles
          ? 'opacity-50 cursor-not-allowed'
          : 'cursor-pointer',
      ]"
      :disabled="disabled || files.length >= maxFiles"
      @click="openPicker"
      @dragenter.prevent="onDragEnter"
      @dragover.prevent="onDragOver"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
    >
      <span
        class="flex items-center justify-center w-10 h-10 shrink-0 rounded-xl bg-primary/10 text-primary"
      >
        <i class="mdi mdi-cloud-upload-outline text-[20px]"></i>
      </span>
      <div class="min-w-0 flex-1">
        <p class="text-xs font-semibold text-slate-700 dark:text-slate-200">
          {{ dropzoneTitle || (lang === "ar" ? "إضافة مرفقات" : "Add attachments") }}
        </p>
        <p class="mt-0.5 text-[10px] text-slate-400 leading-snug">
          {{ hint || dropzoneHint }}
        </p>
        <p class="mt-1 text-[9px] font-medium text-primary/80">
          {{ files.length }}/{{ maxFiles }}
          {{ lang === "ar" ? "ملفات" : "files" }}
        </p>
      </div>
      <span
        class="hidden sm:flex items-center justify-center w-8 h-8 shrink-0 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-400"
      >
        <i class="mdi mdi-paperclip text-[16px]"></i>
      </span>
    </button>
  </div>

  <!-- Default / compact variant -->
  <template v-else>
    <div
      :class="
        compact
          ? 'flex flex-wrap items-center gap-2'
          : 'flex flex-wrap items-center gap-2 px-3 py-2 border rounded-xl border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900'
      "
    >
      <button
        type="button"
        class="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium rounded-lg border border-dashed border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:border-primary hover:text-primary disabled:opacity-50"
        :disabled="disabled || files.length >= maxFiles"
        @click="openPicker"
      >
        <i class="mdi mdi-paperclip text-[14px]"></i>
        {{ addLabel }}
      </button>
      <span v-if="hint && !compact" class="text-[10px] text-slate-400">{{ hint }}</span>
    </div>

    <ul v-if="files.length" :class="compact ? 'mt-1.5 space-y-1' : 'space-y-1'">
      <li
        v-for="(file, idx) in files"
        :key="`${file.name}-${idx}`"
        class="flex items-center justify-between gap-2 px-3 py-1.5 text-[11px] rounded-lg bg-slate-50 dark:bg-slate-800/60"
      >
        <span class="truncate text-slate-700 dark:text-slate-200">
          {{ file.name }}
        </span>
        <button
          type="button"
          class="shrink-0 text-slate-400 hover:text-rose-500"
          :disabled="disabled"
          @click="removeFile(idx)"
        >
          <i class="mdi mdi-close text-[14px]"></i>
        </button>
      </li>
    </ul>
  </template>
  </div>
</template>

<script setup>
import { ref, watch, computed } from "vue";

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  label: { type: String, default: "" },
  hint: { type: String, default: "" },
  addLabel: { type: String, default: "Add files" },
  dropzoneTitle: { type: String, default: "" },
  lang: { type: String, default: "ar" },
  maxFiles: { type: Number, default: 5 },
  maxSizeMb: { type: Number, default: 10 },
  accept: {
    type: String,
    default:
      ".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,image/*,application/pdf",
  },
  disabled: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
  variant: {
    type: String,
    default: "default",
    validator: (v) => ["default", "compact", "dropzone"].includes(v),
  },
});

const emit = defineEmits(["update:modelValue", "error"]);

const fileInput = ref(null);
const files = ref([...props.modelValue]);
const isDragging = ref(false);
let dragCounter = 0;

const dropzoneHint = computed(() =>
  props.lang === "ar"
    ? "صور، PDF، Word، Excel — حتى 10MB لكل ملف"
    : "Images, PDF, Word, Excel — up to 10MB each",
);

watch(
  () => props.modelValue,
  (val) => {
    files.value = [...(val || [])];
  },
);

const allowedExt = [
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "pdf",
  "doc",
  "docx",
  "xls",
  "xlsx",
];

function openPicker() {
  if (!props.disabled && files.value.length < props.maxFiles) {
    fileInput.value?.click();
  }
}

function isAllowed(file) {
  const ext = file.name.split(".").pop()?.toLowerCase() || "";
  return allowedExt.includes(ext);
}

function addFiles(picked) {
  for (const file of picked) {
    if (files.value.length >= props.maxFiles) break;
    if (!isAllowed(file)) {
      emit("error", `Invalid file type: ${file.name}`);
      continue;
    }
    if (file.size > props.maxSizeMb * 1024 * 1024) {
      emit("error", `File too large: ${file.name}`);
      continue;
    }
    files.value.push(file);
  }
  emit("update:modelValue", [...files.value]);
}

function onFileChange(e) {
  const picked = Array.from(e.target.files || []);
  e.target.value = "";
  addFiles(picked);
}

function onDragEnter() {
  if (props.disabled || files.value.length >= props.maxFiles) return;
  dragCounter += 1;
  isDragging.value = true;
}

function onDragOver() {
  if (props.disabled || files.value.length >= props.maxFiles) return;
  isDragging.value = true;
}

function onDragLeave() {
  dragCounter -= 1;
  if (dragCounter <= 0) {
    dragCounter = 0;
    isDragging.value = false;
  }
}

function onDrop(e) {
  dragCounter = 0;
  isDragging.value = false;
  if (props.disabled) return;
  addFiles(Array.from(e.dataTransfer?.files || []));
}

function removeFile(idx) {
  files.value.splice(idx, 1);
  emit("update:modelValue", [...files.value]);
}

function formatSize(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileIcon(file) {
  const name = file.name.toLowerCase();
  if (name.endsWith(".pdf")) return "mdi mdi-file-pdf-box";
  if (/\.(jpg|jpeg|png|gif|webp)$/.test(name)) return "mdi mdi-file-image-outline";
  if (/\.(doc|docx)$/.test(name)) return "mdi mdi-file-word-outline";
  if (/\.(xls|xlsx)$/.test(name)) return "mdi mdi-file-excel-outline";
  return "mdi mdi-file-outline";
}

function fileIconClass(file) {
  const name = file.name.toLowerCase();
  if (name.endsWith(".pdf")) return "bg-rose-50 text-rose-600 dark:bg-rose-900/30 dark:text-rose-300";
  if (/\.(jpg|jpeg|png|gif|webp)$/.test(name))
    return "bg-sky-50 text-sky-600 dark:bg-sky-900/30 dark:text-sky-300";
  if (/\.(doc|docx)$/.test(name))
    return "bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300";
  if (/\.(xls|xlsx)$/.test(name))
    return "bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300";
  return "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400";
}
</script>
